/**
 * The all-entities view: one list across every legal owner the caller acts for.
 *
 * OND-52 part (b), from the walkthrough: "You have the ability in Ondo to add
 * all of those collections together, so you can see all of your collection all
 * in one place, even though the ownership might be in separate entities. And you
 * can toggle those on and off."
 *
 * NOTHING HERE FETCHES, RESOLVES OR DISCLOSES ANYTHING NEW, and that is the
 * whole of why an all-entities view is not a directory. The entities come from
 * the caller's own account read — their principal, plus each organisation they
 * hold an active seat at — and the holdings come from the collections list they
 * could already read, whose owner column arrived with OND-184 and is NULL for
 * anything a stranger shared. So the set of parties this screen can name is
 * exactly the set the caller could already name, and there is no id in it that
 * they did not already hold.
 *
 * It needed no SQL for the same reason. `ondo.owned_collection_ids()` has
 * resolved through `ondo.principal_ids_at_least('admin')` since 20260803110000,
 * so the aggregate across entities was always in one list; what was missing was
 * a screen that said which entity each row belonged to, and a way to take one
 * out of the totals.
 */

import type { CollectionSummary, MeResponse } from "@ondo/shared";

import { MEMBERSHIP_LEVEL_LABELS, ORG_TYPE_LABELS } from "../profile/profile.constants.ts";
import { entityNamesOf, toOwnerRef } from "../lib/works.ts";
import type { EntityNames, OwnerRef } from "../lib/works.ts";
import { ENTITIES_COPY } from "./entities.constants.ts";

/**
 * `entityNamesOf` LIVES IN `lib/works.ts`, not here, and is re-exported for the
 * screen that reads it most.
 *
 * It moved the moment the work record needed the same map: two screens wanting
 * one helper is the rule for `src/lib/`, and a second copy of "which parties may
 * this reader name" is the last thing to keep in two places.
 */
export { entityNamesOf };

/** One legal owner the caller acts for, with what it holds. */
export interface EntityRow {
  /** The entity's own ONDO id — the caller's for their own principal. */
  ondoId: string;
  name: string;
  /** "Person", "Company", "Trust", "Gallery" — whatever kind of owner it is. */
  typeLabel: string;
  /**
   * The caller's rung at the entity, in words, or null for their own
   * principal — where there is no firm and therefore no rung, which is the
   * same null `atLeast` reads as yes.
   */
  levelLabel: string | null;
  /** True for the caller's own principal, which is never a membership. */
  isSelf: boolean;
  collectionCount: number;
  workCount: number;
  /** Whether this entity's holdings are counted in the totals below. */
  counted: boolean;
}

/** One collection, with the entity that holds it. */
export interface HoldingRow {
  id: string;
  name: string;
  workCount: number;
  /** Who holds it: the caller, a named entity, or undisclosed. */
  owner: OwnerRef;
}

/** What the toggles add up to. */
export interface EntityTotals {
  entities: number;
  collections: number;
  works: number;
}

export type EntitiesView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      entities: EntityRow[];
      holdings: HoldingRow[];
      totals: EntityTotals;
      /** Everything turned off, which is a state rather than an empty list. */
      nothingCounted: boolean;
    };

/**
 * The entity rows, with each one's holdings counted.
 *
 * THE CALLER'S OWN PRINCIPAL IS ALWAYS FIRST and is always present, even when
 * they hold nothing: they are a legal owner whether or not they own a picture
 * today, and a list that omitted them would make "all entities" read as "all
 * your companies".
 *
 * `hidden` is the set of ids turned OFF rather than the set turned on, so an
 * entity created or seated while the screen is open arrives counted. The
 * opposite default would silently exclude a company the moment it was made.
 */
export function toEntityRows(
  me: MeResponse,
  collections: CollectionSummary[],
  hidden: ReadonlySet<string>,
): EntityRow[] {
  const rows: EntityRow[] = [
    {
      ondoId: me.principal.ondoId,
      name: ENTITIES_COPY.self,
      typeLabel: ENTITIES_COPY.selfType,
      levelLabel: null,
      isSelf: true,
      ...countsFor(me.principal.ondoId, collections),
      counted: !hidden.has(me.principal.ondoId),
    },
  ];

  for (const membership of me.memberships) {
    if (membership.status !== "active") continue;

    const { ondoId, displayName, orgType } = membership.organisation;
    rows.push({
      ondoId,
      name: displayName,
      typeLabel: ORG_TYPE_LABELS[orgType],
      levelLabel: MEMBERSHIP_LEVEL_LABELS[membership.level],
      isSelf: false,
      ...countsFor(ondoId, collections),
      counted: !hidden.has(ondoId),
    });
  }

  return rows;
}

function countsFor(
  ondoId: string,
  collections: CollectionSummary[],
): { collectionCount: number; workCount: number } {
  const held = collections.filter((collection) => collection.ownerOndoId === ondoId);

  return {
    collectionCount: held.length,
    workCount: held.reduce((total, collection) => total + collection.workCount, 0),
  };
}

/**
 * The collections of the entities currently counted, by name.
 *
 * BY NAME AND NOT BY DATE, which is the one ordering decision here. A list
 * whose point is that it spans several owners is read by looking for a
 * collection, and `createdAt` would interleave three entities' shelves in an
 * order nothing on screen explains.
 *
 * A COLLECTION WITH NO OWNER IS DROPPED, and that is not a redaction being
 * hidden. `ownerOndoId` is null exactly when a stranger shared the collection —
 * `ondo.my_collections()` is SECURITY INVOKER, so the owner grant is not
 * readable — and this screen's question is "what do my entities hold". A row
 * whose holder is undisclosed belongs to somebody else's entity and could not
 * be toggled by anything on this screen, so counting it would make the totals
 * mean something other than what the heading says.
 */
export function toHoldingRows(
  collections: CollectionSummary[],
  entities: EntityNames,
  hidden: ReadonlySet<string>,
): HoldingRow[] {
  return collections
    .filter((collection) => {
      const owner = collection.ownerOndoId;
      return owner !== null && entities.has(owner) && !hidden.has(owner);
    })
    .map((collection) => ({
      id: collection.id,
      name: collection.name,
      workCount: collection.workCount,
      owner: toOwnerRef(collection, entities),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * What the counted entities add up to.
 *
 * Summed from the ENTITY rows rather than from the holdings, so the three
 * numbers cannot disagree with the list above them: one collection belongs to
 * one entity, which is OND-40's "one owner per work, everywhere" one level up.
 */
export function totalsOf(entities: EntityRow[]): EntityTotals {
  const counted = entities.filter((entity) => entity.counted);

  return {
    entities: counted.length,
    collections: counted.reduce((total, entity) => total + entity.collectionCount, 0),
    works: counted.reduce((total, entity) => total + entity.workCount, 0),
  };
}

/** The whole view, assembled. */
export function resolveEntities(input: {
  me: MeResponse;
  collections: CollectionSummary[];
  hidden: ReadonlySet<string>;
}): EntitiesView {
  const entities = toEntityRows(input.me, input.collections, input.hidden);
  const names = entityNamesOf(input.me);

  return {
    kind: "ready",
    entities,
    holdings: toHoldingRows(input.collections, names, input.hidden),
    totals: totalsOf(entities),
    nothingCounted: entities.every((entity) => !entity.counted),
  };
}

/**
 * Whether a name will be accepted, mirroring `createEntitySchema`.
 *
 * A MIRROR AND NOT A SECOND OPINION, exactly as `atLeast` says of itself: the
 * database decides, and this exists so pressing the button on an empty field
 * is a sentence rather than a round trip.
 */
export function validateEntityName(name: string): string | null {
  return name.trim() === "" ? ENTITIES_COPY.nameRequired : null;
}
